// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useMemo, useCallback, useState, useEffect, type MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import {
  selectClient,
  selectClientId,
} from '@shared/data-access/slices/clients/clients-selectors';
import { clientsSlice } from '@shared/data-access/slices/clients/clients-slice';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { CLIENTS_PAGE_SIZE } from '@shared/advisory/aggregation/hooks/use-clients';
import type { ThunkDispatch } from '@reduxjs/toolkit';
import type { SelectedClient } from '@shared/data-access/slices/clients/clients-state';

export const useClientProfileSelector = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pickerPage, setPickerPage] = useState(1);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const id = useMemo(() => (open ? 'client-profile-popover' : undefined), [open]);

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const selectedClient = useSelector(selectClient);

  const selectedClientId = useSelector(selectClientId);

  const currency = useSelector(selectAccountCurrency);

  const { data: clientsPage, isFetching } = advisoryApi.useGetAdvisoryClientsQuery(
    { page: pickerPage, page_size: CLIENTS_PAGE_SIZE, currency },
    { skip: !open },
  );

  useEffect(() => {
    if (open) {
      setPickerPage(1);
    }
  }, [open]);

  const selectedClientProfile = useMemo(() => {
    if (!selectedClient) {
      return undefined;
    }

    const label = selectedClient.name || selectedClient.email.split('@')[0];

    return {
      ...selectedClient,
      avatar: label?.charAt(0).toUpperCase(),
    };
  }, [selectedClient]);

  const profiles = useMemo(() => {
    const rows = (clientsPage?.data ?? []).map(
      ({ id, name, email, citec_user_id, risk_profile, status }) => ({
        value: id,
        label: name || email.split('@')[0],
        email,
        client: {
          id,
          name,
          email,
          citec_user_id,
          risk_profile,
          status,
        } satisfies SelectedClient,
      }),
    );

    if (!selectedClient) {
      return rows;
    }

    const freshRow = rows.find(row => row.value === selectedClient.id);

    const pinned = freshRow ?? {
      value: selectedClient.id,
      label: selectedClient.name || selectedClient.email.split('@')[0],
      email: selectedClient.email,
      client: {
        id: selectedClient.id,
        name: selectedClient.name,
        email: selectedClient.email,
        citec_user_id: selectedClient.citec_user_id,
        risk_profile: selectedClient.risk_profile,
        status: selectedClient.status,
      },
    };

    const rest = rows.filter(row => row.value !== selectedClient.id);

    return [pinned, ...rest];
  }, [clientsPage?.data, selectedClient]);

  const onClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onSelectProfile = useCallback(
    (row: SelectedClient) => {
      dispatch(clientsSlice.actions.setClient(row));
      onClose();
    },
    [dispatch, onClose],
  );

  const pickerPageCount = clientsPage?.pages ?? 1;

  const onPreviousPage = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setPickerPage(current => Math.max(current - 1, 1));
  }, []);

  const onNextPage = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setPickerPage(current => Math.min(current + 1, pickerPageCount));
    },
    [pickerPageCount],
  );

  return {
    profiles,
    activeClientId: selectedClientId,
    onSelectProfile,
    selectedClientProfile,
    anchorEl,
    open,
    id,
    onClick,
    onClose,
    pickerPage,
    pickerPageCount,
    isFetchingProfiles: isFetching,
    onPreviousPage,
    onNextPage,
  } as const;
};
